import React, { useMemo } from "react";
import { GridFour } from "@phosphor-icons/react";
import { TeamLogo } from "@/components/TeamVisuals";
import { teamTextColor } from "@/lib/teams";

// Sprint 69 — how often each QB in the book is paired with each of his
// team's pass-catchers (WR/TE/RB), plus the bring-back share (opponent
// pass-catcher in the same lineup). Cell = % of that QB's lineups.
const heat = (pct) => {
  if (pct <= 0) return { background: "transparent", color: "#71717a" };
  const a = Math.min(1, 0.12 + pct / 100);
  // text inherits the theme below 35% (subtle tint), flips to black once the cell is bright
  return { background: `rgba(57, 255, 20, ${a * 0.6})`, color: pct >= 35 ? "#000" : "inherit" };
};

export const TeamStackHeatmap = ({ lineups = [], opponentOf = {} }) => {
  const rows = useMemo(() => {
    const oppMap = { ...opponentOf };
    for (const L of lineups) for (const p of L.players || []) {
      const o = p.opponent || p.opp;
      if (o && p.team && !oppMap[String(p.team)]) oppMap[String(p.team)] = String(o);
    }
    const byQb = new Map();
    for (const L of lineups) {
      const ps = L.players || [];
      const qbs = ps.filter((p) => String(p.position).toUpperCase() === "QB");
      for (const qb of qbs) {
        const id = String(qb.dk_id);
        const row = byQb.get(id) || { id, name: qb.name, team: qb.team, n: 0, mates: new Map(), bringBack: 0, naked: 0 };
        row.n += 1;
        const opp = oppMap[String(qb.team)] || "";
        let mates = 0;
        for (const p of ps) {
          if (String(p.dk_id) === id || !["WR", "TE", "RB"].includes(String(p.position).toUpperCase())) continue;
          if (p.team === qb.team) {
            const m = row.mates.get(String(p.dk_id)) || { id: String(p.dk_id), name: p.name, position: p.position, n: 0 };
            m.n += 1;
            row.mates.set(String(p.dk_id), m);
            mates += 1;
          } else if (opp && p.team === opp) {
            row.bringBack += 1;
          }
        }
        if (!mates) row.naked += 1;
        byQb.set(id, row);
      }
    }
    return [...byQb.values()]
      .map((r) => ({ ...r, mates: [...r.mates.values()].sort((a, b) => b.n - a.n).slice(0, 6) }))
      .sort((a, b) => b.n - a.n);
  }, [lineups, opponentOf]);

  if (!rows.length || lineups.some((L) => (L.players || []).some((p) => p.roster_slot === "CPT"))) return null;
  const width = Math.max(...rows.map((r) => r.mates.length), 1);
  return (
    <section data-testid="team-stack-heatmap" className="border border-zinc-800 bg-[#0b0b0b] p-4">
      <div className="flex items-center gap-2 mb-3">
        <GridFour size={14} weight="fill" className="text-[#39FF14]" />
        <h3 className="mono text-[10px] uppercase tracking-widest text-zinc-400">Team stack heatmap · {lineups.length} lineups</h3>
        <span className="mono text-[9px] text-zinc-600 ml-auto">cell = % of that QB's lineups pairing him with the player</span>
      </div>
      <div className="overflow-x-auto wp-scroll">
        <table className="w-full text-[11px]" data-testid="team-stack-heatmap-table">
          <thead>
            <tr className="mono text-[9px] uppercase tracking-widest text-zinc-500">
              <th className="text-left px-2 py-1">QB · lineups</th>
              {Array.from({ length: width }).map((_, i) => <th key={i} className="px-2 py-1 text-left">Mate {i + 1}</th>)}
              <th className="px-2 py-1 text-right">Bring-back</th>
              <th className="px-2 py-1 text-right">Naked</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} data-testid={`stack-heat-row-${r.id}`} className="border-t border-zinc-900">
                <td className="px-2 py-1.5 whitespace-nowrap">
                  <span className="inline-flex items-center gap-2">
                    <TeamLogo team={r.team} size={20} />
                    <span className="text-white">{r.name}</span>
                    <span className="mono font-black text-[10px]" style={{ color: teamTextColor(r.team) }}>{r.team}</span>
                    <span className="mono text-[10px] text-zinc-500">· {r.n}</span>
                  </span>
                </td>
                {Array.from({ length: width }).map((_, i) => {
                  const m = r.mates[i];
                  if (!m) return <td key={i} className="px-2 py-1.5" />;
                  const pct = (100 * m.n) / r.n;
                  return (
                    <td key={m.id} className="px-1 py-1">
                      <div className="px-2 py-1 mono text-[10px] whitespace-nowrap" style={heat(pct)} title={`${m.name} in ${m.n}/${r.n} of ${r.name}'s lineups`}
                           data-testid={`stack-heat-cell-${r.id}-${m.id}`}>
                        <span className="text-[9px] opacity-70">{m.position}</span> {m.name} <span className="font-black">{pct.toFixed(0)}%</span>
                      </div>
                    </td>
                  );
                })}
                <td className="px-2 py-1.5 text-right mono" style={heat((100 * r.bringBack) / r.n)}>{((100 * r.bringBack) / r.n).toFixed(0)}%</td>
                <td className={`px-2 py-1.5 text-right mono ${r.naked ? "text-[#FFCC00]" : "text-zinc-600"}`}>{((100 * r.naked) / r.n).toFixed(0)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default TeamStackHeatmap;
